import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import API_BASE_URL, { API_IMAGE_BASE_URL } from '../../api/config';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [imgID, setImgID] = useState(0);
  const [disp, setDisp] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [isClosingDetails, setIsClosingDetails] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [registeredEvents, setRegisteredEvents] = useState([]);
  const [guestEmail, setGuestEmail] = useState("");

  useEffect(() => {
    if (showModal) {
      document.body.classList.add('modal-open-body');
    } else {
      document.body.classList.remove('modal-open-body');
    }
    // Cleanup on component unmount
    return () => {
      document.body.classList.remove('modal-open-body');
    };
  }, [showModal, showDetailsModal]);


  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/upcoming_events/read_isShown`);
      const data = response.data;

      let eventsData = data;
      if (!Array.isArray(eventsData) && eventsData.data && Array.isArray(eventsData.data)) {
        eventsData = eventsData.data;
      } else if (!Array.isArray(eventsData)) {
        throw new Error(`Unexpected response format: ${JSON.stringify(data)}`);
      }

      const filtered = eventsData.map((e) => ({
        ...e,
        title: e.name,
        fullDate: e.date,
        img: `${API_IMAGE_BASE_URL}/${e.banner_image1}`,
        poster_img: `${API_IMAGE_BASE_URL}/${e.Poster_Image}`,
        description: e.description,
        need_qr_tickets: Number(e.Need_Qr_Tickets),
      }));
      setEvents(filtered);
    } catch (err) {
      console.error("[UpcomingEvents] Failed to load:", err?.response?.data ?? err?.message ?? err);
      setError("Failed to load upcoming events. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchRegisteredEvents = useCallback(async () => {
    try {
      const useremail = getCookie('email');
      if (!useremail) return;

      const response = await axios.get(`${API_BASE_URL}/api/event_bookings/check_user_bookings?email=${useremail}`, {
        withCredentials: true
      });

      const arr = Array.isArray(response.data) ? response.data : (response.data.data || []);
      if (Array.isArray(arr)) {
        // Convert each event_id to a string during state update
        setRegisteredEvents(arr.map(e => String(e.event_id)));
      }
    } catch (error) {
      console.error("Failed to fetch registered events:", error);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
    fetchRegisteredEvents();
  }, [fetchEvents, fetchRegisteredEvents]);

  const toggleOverlay = useCallback((id) => {
    setImgID(id);
    setDisp(true);
  }, []);

  const openPreview = (event) => {
    setSelectedEvent(event);
    setShowDetailsModal(true);
    setIsClosingDetails(false);
  };

  const closePreview = () => {
    setIsClosingDetails(true);
  };

  const onDetailsAnimationEnd = () => {
    if (isClosingDetails) {
      setShowDetailsModal(false);
      setIsClosingDetails(false);
    }
  };

  const handleGetTickets = async (event) => {

    // 1️⃣ Check if event form is closed
    if (event.isOpen !== "1") {
      Swal.fire({
        icon: 'info',
        title: 'Form Closed',
        text: 'Ticket booking for this event is currently closed.',
      });
      return;
    }

    const needLogin = Number(event.need_login) === 1;
    const useremail = getCookie('email');

    // 2️⃣ If login required but user not logged in
    if (needLogin && !useremail) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'Please login first to book this event.',
        confirmButtonText: 'Login'
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = "/register";
        }
      });
      return;
    }

    // 3️⃣ If login required → check profile completion
    if (needLogin) {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/profile/read_byEmail?email=${useremail}`,
          { withCredentials: true }
        );

        const userProfile = response.data.data;

        if (!userProfile || !userProfile.full_name || userProfile.full_name.trim() === "") {
          Swal.fire({
            icon: 'info',
            title: 'Profile Incomplete',
            text: 'Please complete your profile before booking tickets.',
            confirmButtonText: 'Go to Profile'
          }).then((result) => {
            if (result.isConfirmed) {
              window.location.href = "/dashboard/profileEdit";
            }
          });
          return;
        }

      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch your profile.',
        });
        return;
      }
    }

    // 4️⃣ Open form if all checks pass
    setSelectedEvent(event);
    setAnswers({});
    setGuestEmail("");
    setShowModal(true);
  };

  const getCookie = (name) => {
    const cookieString = document.cookie;
    const cookies = cookieString.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.startsWith(name + '=')) {
        return cookie.substring(name.length + 1);
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const useremail = getCookie('email');
    const needLogin = Number(selectedEvent.need_login) === 1;
    
    if (needLogin) {
      // Step 1: No email → redirect to register
      if (!useremail) {
        Swal.fire({
          icon: 'warning',
          title: 'Signin Required',
          text: 'Please sign in first to book the event.',
          confirmButtonText: 'Lets Signup'
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = '/register'; // redirect to register
          }
        });
        return;
      }

      // Step 2: Fetch user profile using email
      let userProfile;
      try {
        const response = await axios.get(`${API_BASE_URL}/api/profile/read_byEmail?email=${useremail}`, {
          withCredentials: true
        });
        userProfile = response.data.data;
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to fetch your profile. Please try again.',
        });
        return;
      }

      // Step 3: Check if profile is complete (has FullName)
      if (!userProfile || !userProfile.full_name || userProfile.full_name.trim() === "") {
        Swal.fire({
          icon: 'info',
          title: 'Profile Incomplete',
          text: 'Please complete your profile before booking tickets.',
          confirmButtonText: 'Go to Profile'
        }).then((result) => {
          if (result.isConfirmed) {
            window.location.href = '/dashboard/profileEdit'; // redirect to profile edit
          }
        });
        return;
      }
    }

    // 👇 Guest email validation
    if (Number(selectedEvent.need_login) === 0) {
      if (!guestEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail)) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Email',
          text: 'Please enter a valid email address to continue.',
        });
        return;
      }
    }

    // Step 4: Existing validation for mandatory questions
    for (let i = 1; i <= 30; i++) {
      const isMandatory = selectedEvent[`qmand${i}`] === "1";
      const type = selectedEvent[`questionType${i}`];
      const qKey = `q${i}`;
      const value = answers[qKey];

      if (isMandatory) {
        if (
          value == null ||
          (typeof value === 'string' && value.trim() === '') ||
          (type === "mcq" && (!value || value === ""))
        ) {
          Swal.fire({
            icon: 'error',
            title: 'Incomplete Form',
            text: `Please answer mandatory Question ${i} before submitting.`,
          });
          return;
        }
      }
    }

    // Step 5: Submit booking
    setLoading(true);
    try {
      const formattedAnswers = {};
      for (let i = 1; i <= 30; i++) {
        const qKey = `q${i}`;
        formattedAnswers[`answer${i}`] = answers[qKey] ?? null;
      }

      const payload = {
        event_name: selectedEvent.name,
        whatsapp_link: selectedEvent.whatsapp_link,
        event_id: selectedEvent.id,
        need_login: Number(selectedEvent.need_login),
        need_qr_tickets: Number(selectedEvent.need_qr_tickets),
        guest_email: Number(selectedEvent.need_login) === 0 ? guestEmail : null,
        ...formattedAnswers
      };

      const res = await fetch(`${API_BASE_URL}/api/event_bookings/upload`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload)
      });

      const text = await res.text();
      let json = {};
      try { json = JSON.parse(text); } catch(e) { throw new Error(`Response not valid JSON: ${text}`); }

      if (res.ok) {
        setShowModal(false);
        if (selectedEvent.whatsapp_link) {
          window.location.href = `${selectedEvent.whatsapp_link}`;
        }
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful 🎉',
          text: json.message || 'You have successfully Registered!',
          confirmButtonText: 'Continue'
        }).then(() => {
          // 👇 show WhatsApp link after success confirmation
          // if (selectedEvent.whatsapp_link) {
          //   Swal.fire({
          //     icon: 'info',
          //     title: 'Join the WhatsApp Group',
          //     html: `
          //       <p>Stay updated about the event 👇</p>
          //       <a 
          //         href="${selectedEvent.whatsapp_link}" 
          //         target="_blank" 
          //         style="color:#4f46e5; font-weight:600;"
          //       >
          //         Join WhatsApp Group
          //       </a>
          //     `,
          //     confirmButtonText: 'Got it'
          //   });
          // }
          // if (selectedEvent.whatsapp_link) {
          //   window.location.href = `${selectedEvent.whatsapp_link}`;
          // }
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Error!', text: json.error || "Failed to book the event." });
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Error!', text: error.message || "An unexpected error occurred." });
    } finally {
      setLoading(false);
    }
  };

  const renderQuestions = () => {
    const questions = [];

    // Helper styles
    const styles = {
      card: {
        background: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        border: "1px solid rgba(0,0,0,0.04)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        // Enforce block formatting to prevent overlap
        display: "block", 
        position: "relative",
      },
      qBadge: {
        background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
        color: "white",
        padding: "5px 12px",
        borderRadius: "8px",
        fontSize: "0.85rem",
        fontWeight: "700",
        marginRight: "12px",
        boxShadow: "0 2px 10px rgba(99, 102, 241, 0.3)",
      },
      inputField: {
        marginTop: "15px",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
        padding: "14px",
        fontSize: "0.95rem",
        backgroundColor: "#f8fafc",
        transition: "all 0.2s",
      },
      optionTile: (isActive) => ({
        cursor: "pointer",
        padding: "12px 16px",
        borderRadius: "10px",
        border: isActive ? "2px solid #6366f1" : "1px solid #e2e8f0",
        backgroundColor: isActive ? "#eff6ff" : "#ffffff",
        display: "flex",
        alignItems: "center",
        transition: "all 0.2s ease",
        fontWeight: isActive ? "600" : "400",
        color: isActive ? "#4338ca" : "#475569",
      }),
    };

    // 👇 Inject Email Question for Guest Users
    if (Number(selectedEvent.need_login) === 0) {
      questions.push(
        <div
          key="guest-email"
          className="mb-5 question-item"
          style={{
            ...styles.card,
            padding: "12px",
            marginBottom: "1rem",
          }}
        >
          <div className="d-flex align-items-start mb-3">
            <span style={styles.qBadge}> Q1 </span>
            <label
              className="flex-grow-1"
              style={{
                fontSize: "1.05rem",
                fontWeight: "600",
                color: "#1e293b",
              }}
            >
              Email Address <span style={{ color: "#ef4444" }}>*</span>
            </label>
          </div>

          <input
            type="email"
            className="form-control"
            placeholder="Enter your email address"
            value={guestEmail}
            onChange={(e) => setGuestEmail(e.target.value)}
            style={{
              borderRadius: "12px",
              padding: "14px",
              backgroundColor: "#f8fafc",
              marginTop: "15px",
            }}
          />
        </div>
      );
    }

    for (let i = 1; i <= 30; i++) {
      const qKey = `question${i}`;
      const typeKey = `questionType${i}`;
      let question = selectedEvent[qKey];
      const type = selectedEvent[typeKey];

      if (!question) continue;

      const name = `q${i}`;
      const isMandatory = selectedEvent[`qmand${i}`] === "1";
      const optionRegex = /{(.*?)}/;
      const match = question.match(optionRegex);
      let rawOptions = selectedEvent[`options${i}`];
      let options = [];
        if (Array.isArray(rawOptions)) {
          options = rawOptions;
        } else if (typeof rawOptions === "string" && rawOptions.trim() !== "") {
          try {
            options = JSON.parse(rawOptions);
          } catch (e) {
            console.error(`Invalid JSON in options${i}`, rawOptions);
            options = [];
          }
        }
      const questionText = match ? question.replace(optionRegex, "").trim() : question;
      questions.push(
        <div
          key={i}
          className="mb-5 question-item" // Increased to mb-5
          style={{
            ...styles.card,
            padding: "12px",
            marginBottom: "1rem", // Added explicit margin to prevent overlap
            animationDelay: `${i * 60}ms`,
          }}
        >
          {/* Question Header */}
          <div className="d-flex align-items-start mb-2">
            <span style={styles.qBadge}>Q{i+ 1}</span>
            <label
              className="Ticket_Question flex-grow-1"
              style={{
                fontSize: "1.05rem",
                color: "#1e293b",
                lineHeight: "1.6",
                fontWeight: "600",
              }}
            >
              {questionText}
              {isMandatory && <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>}
            </label>
          </div>
          <div className="mt-2">
            {/* Text Input */}
            {type === "text" && (
              <div className="mt-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder='Write Your Answer here'   
                  value={answers[name] || ""}  
                  onChange={(e) =>
                    setAnswers({ ...answers, [name]: e.target.value })
                  }
                  style={{
                    ...styles.inputField,
                    boxShadow: "none",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#6366f1")}
                  onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                />
              </div>
            )}

            {/* Radio Input (Styled as Tiles) */}
            {type === "radio" && options.length > 0 && (
              <div className="d-flex flex-column gap-2 mt-2">
                {options.map((opt, idx) => {
                  const isSelected = answers[name] === opt;
                  return (
                    <div
                      key={idx}
                      onClick={() => setAnswers({ ...answers, [name]: opt })}
                      style={styles.optionTile(isSelected)}
                    >
                      <div
                        style={{
                          height: "18px",
                          width: "18px",
                          borderRadius: "50%",
                          border: isSelected ? "5px solid #6366f1" : "2px solid #cbd5e1",
                          marginRight: "12px",
                          flexShrink: 0,
                        }}
                      />
                      <span>{opt}</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rating Input */}
            {type === "rating" && (
              <div
                className="d-flex gap-3 mt-2 p-3 rounded-3"
                style={{ backgroundColor: "#f8fafc", width: "fit-content" }}
              >
                {[1, 2, 3, 4, 5].map((val) => {
                  const isActive = answers[name] >= val;
                  return (
                    <span
                      key={val}
                      role="button"
                      onClick={() => setAnswers({ ...answers, [name]: val })}
                      style={{
                        cursor: "pointer",
                        fontSize: "2rem",
                        transition: "transform 0.15s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        color: isActive ? "#f59e0b" : "#cbd5e1",
                        transform: isActive ? "scale(1.1)" : "scale(1)",
                        lineHeight: 1,
                      }}
                    >
                      ★
                    </span>
                  );
                })}
              </div>
            )}

            {/* MCQ / Dropdown (Styled as Tiles) */}
            {type === "mcq" && options.length > 0 && (
              <select
                className="form-select"
                value={answers[name] || ""}
                onChange={(e) =>
                  setAnswers({ ...answers, [name]: e.target.value })
                }
                style={{
                  ...styles.inputField,
                  maxWidth: "100%",
                  cursor: "pointer",
                }}
              >
                <option value="" disabled>
                  Select an option
                </option>

                {options.map((opt, idx) => (
                  <option key={idx} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            )}
          </div> 
        </div>
      );
    }
    return questions;
  };

  if (isLoading) {
    return <div className="alert alert-info" style={{ textAlign: 'center', padding: '2rem' }}>Loading upcoming events...</div>;
  }

  if (error) {
    return (
      <div className="alert alert-danger" style={{ textAlign: 'center', padding: '2rem' }}>
        <p>{error}</p>
        <button
          onClick={fetchEvents}
          style={{
            marginTop: '10px',
            padding: '8px 20px',
            borderRadius: '8px',
            border: 'none',
            background: '#6366f1',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (events.length === 0) {
    return <div className="alert alert-info" style={{ textAlign: 'center', padding: '2rem' }}>No upcoming events at the moment.</div>;
  }

  return (
    <section className="upcoming-events">
      {disp && (
        <div className="overlay show" onClick={() => setDisp(false)}>
          <img
            src={events[imgID]?.img}
            alt="Event Banner"
            className="full-img fade-in"
          />
        </div>
      )}

      <div className="heading-wrapper">
        <h2>
          <span className="upcoming-title">UPCOMING</span> <span className="fest-title">EVENTS</span>
        </h2>
        <div className="header-underline"></div>
      </div>

      <div className="three-cards">
        <div className="row">
          {events.map((event, index) => (
            <div className="col-lg-4 col-md-6 col-sm-12 card-padding" key={index} style={{ "--i": index + 1 }}>
              <div className="card">
                <div className="card-img">
                  <img
                    src={event.img}
                    alt={`${event.title} Banner`}
                    className="event-img"
                    onClick={() => toggleOverlay(index)}
                  />
                </div>
                <div className="card-text">
                  <p className="event-name">{event.title}</p>
                  <div className="small-text">
                    {event.fullDate && event.fullDate !== "null" && (
                      <p><strong>Date:</strong> {event.fullDate}</p>
                    )}
                    {event.venue && event.venue !== "null" && (
                      <p><strong>Venue:</strong> {event.venue}</p>
                    )}
                    <div className="button-group">
                      {/* View Details button */}
                      <button
                        className="view-details-btn"
                        onClick={() => openPreview(event)}
                      >
                        <b>View Details</b>
                      </button>
                      {registeredEvents.includes(String(event.id)) ? (
                        <button className="registered-btn" disabled> Registered </button>
                      ) : (
                        <button className="Rd get-tickets-btn" onClick={() => handleGetTickets(event)}>
                          <b>Register</b>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {showDetailsModal && selectedEvent && (
        <div
          className={`premium-overlay ${isClosingDetails ? "closing" : ""}`}
          onClick={closePreview}
        >
          <div
            className={`premium-modal ${isClosingDetails ? "closing" : ""}`}
            onClick={(e) => e.stopPropagation()}
            onAnimationEnd={onDetailsAnimationEnd}
          >
            {/* Close Button */}
            <button className="premium-close-btn" onClick={closePreview}>
              <span className="close-svg">&times;</span>
            </button>

            <div className="premium-modal-layout">
              {/* Left Side: Image & Visuals */}
              <div className="premium-visual-col">
                <div className="visual-wrapper">
                  <img src={selectedEvent.poster_img} alt={selectedEvent.title} className="premium-img" />
                  <div className="visual-gradient"></div>
                  <div className="visual-badges">
                     {/* Dynamic Badge Example */}
                    <span className="event-badge">
                      {selectedEvent.college === "main" ? "Flagship Event" : selectedEvent.college}
                    </span>
                  </div>
                </div>
                {/* Footer Action */}
                <div className="premium-footer">
                   {registeredEvents.includes(String(selectedEvent.id)) ? (
                      <button className="premium-cta-btn registered" disabled>
                        <span>Already Registered</span>
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                      </button>
                   ) : (
                      <button
                        className="premium-cta-btn"
                        onClick={() => {
                          setIsClosingDetails(true);

                          setTimeout(() => {
                            setShowDetailsModal(false);   
                            setIsClosingDetails(false);
                            handleGetTickets(selectedEvent); 
                          }, 350); 
                        }}
                      >
                        <span>Get Tickets Now</span>
                        <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                      </button>
                   )}
                </div>
              </div>

              {/* Right Side: Content & Actions */}
              <div className="premium-content-col">
                <div className="content-header">
                  <h2 className="premium-title">{selectedEvent.title}</h2>
                  <div className="premium-divider"></div>
                </div>

                {/* Info Cards Grid */}
                <div className="premium-info-grid">
                  <div className="info-card">
                    <div className="icon-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                    </div>
                    <div className="info">
                      <span className="info-label">Date</span>
                      <p className="info-val">{selectedEvent.fullDate}</p>
                    </div>
                  </div>
                  
                  <div className="info-card">
                    <div className="icon-box">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>
                    </div>
                    <div className="info">
                      <span className="info-label">Venue</span>
                      <p className="info-val">{selectedEvent.venue}</p>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="premium-description">
                  <h3>About the Event</h3>
                  <p>{selectedEvent.description}</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}
      {showModal && selectedEvent && (
  <div
    className="modal-single-container"
    style={{
      // 1. Positioning: Fixed & Centered
      position: "fixed",
      top: "50%",
      zIndex: 1050,
      left: "50%",
      transform: "translate(-50%, -50%)", // Perfect centering

      // 2. Sizing
      width: "90%",
      maxWidth: "800px", // Increased from 600px
      maxHeight: "90vh", // Prevents it from being taller than screen

      // 3. Premium Styling
      backgroundColor: "#ffffff",
      borderRadius: "20px",
      display: "flex",
      flexDirection: "column", // Stack Header, Body, Footer
      
      // 4. THE TRICK: Massive shadow creates the dark backdrop
      boxShadow: "0 20px 60px rgba(0,0,0,0.15), 0 0 0 100vmax rgba(0,0,0,0.6)",
    }}
  >
    {/* --- Header --- */}
    <div 
      style={{ 
        padding: "12px 28px", 
        borderBottom: "1px solid #f1f5f9",
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center" 
      }}
    >
      <h3 style={{ margin: 0, fontSize: "1.25rem", color: "#0f172a", fontWeight: "700" }}>
        {selectedEvent.name}
      </h3>
      <button
        onClick={() => setShowModal(false)}
        style={{
          background: "transparent",
          border: "none",
          fontSize: "24px",
          color: "#94a3b8",
          cursor: "pointer",
          lineHeight: 1,
          padding: 0
        }}
      >
        &times;
      </button>
    </div>

    {/* --- Scrollable Body --- */}
    <div 
      style={{ 
        padding: "20px", 
        overflowY: "auto",    // Scroll the questions list
        flex: "1 1 auto",     // Flex trick: adapt to content, shrink if needed
        minHeight: 0,         // CRITICAL: without this, flex child ignores overflow and grows past maxHeight
        paddingBottom: "8px",
      }}
    >
      {renderQuestions()}
    </div>

    {/* --- Footer --- */}
    <div 
      style={{ 
        padding: "12px 18px", 
        borderTop: "1px solid #f1f5f9",
        display: "flex", 
        gap: "12px", 
        justifyContent: "flex-end",
        backgroundColor: "#f8fafc", // Slight contrast for footer
        borderRadius: "0 0 20px 20px"
      }}
    >
      <button
        onClick={() => setShowModal(false)}
        style={{
          padding: "10px 20px",
          borderRadius: "10px",
          border: "1px solid #e2e8f0",
          background: "white",
          color: "#475569",
          fontWeight: "600",
          cursor: "pointer",
          transition: "all 0.2s"
        }}
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading}
        style={{
          padding: "10px 24px",
          borderRadius: "10px",
          border: "none",
          background: loading ? "#94a3b8" : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
          color: "white",
          fontWeight: "600",
          cursor: loading ? "not-allowed" : "pointer",
          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)"
        }}
      >
        {loading ? "Booking..." : "Submit"}
      </button>
    </div>
  </div>
)}
    </section>
  );
};

export default UpcomingEvents;